// The one place this app calls `fetch`.
//
// Everything else goes through TanStack Query hooks over these functions, which
// means the API conventions — the query-string shape, error-envelope decoding,
// same-origin credentials, `If-Match` on every PATCH, `Idempotency-Key` on
// every POST — are implemented once. A component that built its own request
// would be the component that forgets `If-Match` and gets a 428, or the one
// that quietly stops sending the session cookie (§05).

import type { z } from "zod";
import { ApiProblem, toErrorResponse, transportProblem } from "./problem.js";
import { buildQueryString, type QueryParams } from "./queryString.js";

/**
 * Relative on purpose. §04: in production the API serves the built SPA from its
 * own origin, and the dev server proxies `/api` to it, so the browser sees one
 * origin in both. An absolute base URL would reintroduce CORS and cookie-domain
 * differences between dev and prod — the class of bug that only shows up after
 * deploy.
 */
const API_BASE = "/api";

export interface ApiGetOptions {
  params?: QueryParams;
  signal?: AbortSignal;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Not JSON: an ingress error page or a proxy's plain-text complaint. Handed
    // through as a string so `toErrorResponse` can quote it in the message.
    return text;
  }
}

/**
 * Validate against the shared schema, but never let a validation failure blank
 * the screen.
 *
 * The API and the UI derive from the same `@crm/shared` schemas, so a mismatch
 * means one side drifted — worth a loud console warning during development.
 * It is NOT worth refusing to render: a newly added nullable column the UI
 * doesn't know about yet would otherwise take out every screen that reads that
 * resource. Same reasoning as the main app's `lenientParse`.
 */
export function lenientParse<S extends z.ZodType>(
  schema: S,
  data: unknown,
  path: string,
): z.infer<S> {
  const result = schema.safeParse(data);
  if (result.success) return result.data;
  console.warn(
    `[crm-api] response from ${path} does not match the shared contract`,
    result.error.issues,
  );
  return data as z.infer<S>;
}

/**
 * Called whenever the API answers 401 to anything other than the session
 * endpoint itself.
 *
 * §05 keeps the session in an opaque cookie the SPA cannot read, so the ONLY
 * way this app learns its session died is a request failing. Without this, an
 * expired session shows up as every panel on the screen failing individually
 * with "unauthorized" — the user is left to infer that they have been signed
 * out. The provider registers a listener that invalidates the session query, so
 * one 401 drops the whole UI to the signed-out screen.
 */
type UnauthorizedListener = () => void;
let unauthorizedListener: UnauthorizedListener | null = null;

export function setUnauthorizedListener(listener: UnauthorizedListener | null): void {
  unauthorizedListener = listener;
}

/** `GET /api/auth/session` answers 401 as a NORMAL result: "nobody is signed in". */
export const AUTH_PATH_PREFIX = "/auth/";

function noteUnauthorized(path: string, status: number): void {
  if (status !== 401) return;
  // A 401 from the session probe is the answer, not a failure — reacting to it
  // would invalidate the very query that produced it, forever.
  if (path.startsWith(AUTH_PATH_PREFIX)) return;
  unauthorizedListener?.();
}

interface RawResponse {
  response: Response;
  body: unknown;
  url: string;
}

async function request(url: string, path: string, init: RequestInit): Promise<RawResponse> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: { Accept: "application/json", ...init.headers },
      // The session cookie is same-origin (§05); this is explicit so it stays
      // true if the SPA is ever served from somewhere else by accident.
      credentials: "same-origin",
    });
  } catch (cause) {
    throw transportProblem(url, cause);
  }

  const body = await readBody(response);

  if (!response.ok) {
    noteUnauthorized(path, response.status);
    throw new ApiProblem(
      toErrorResponse({ status: response.status, statusText: response.statusText, body, url }),
      response.status,
    );
  }

  return { response, body, url };
}

/** GET a resource or report. */
export async function apiGet<S extends z.ZodType>(
  schema: S,
  path: string,
  options: ApiGetOptions = {},
): Promise<z.infer<S>> {
  const url = `${API_BASE}${path}${buildQueryString(options.params ?? {})}`;
  const { body } = await request(url, path, {
    method: "GET",
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  return lenientParse(schema, body, path);
}

export interface RawGetResult {
  readonly status: number;
  readonly body: unknown;
}

/**
 * GET an operation that declares TWO success arms, and hand the caller the
 * status with the body.
 *
 * `reportUsageCost` answers 200 with the breakdown or 202 with the job that is
 * filling the scope. Which arm answered is the HTTP status and not a field on a
 * body, so the caller maps the status to the schema and returns a discriminated
 * union. Everything else — the query string, the credentials, the 401 listener,
 * the error envelope — is the same code as `apiGet`.
 */
export async function apiGetRaw(path: string, options: ApiGetOptions = {}): Promise<RawGetResult> {
  const url = `${API_BASE}${path}${buildQueryString(options.params ?? {})}`;
  const { response, body } = await request(url, path, {
    method: "GET",
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  return { status: response.status, body };
}

export interface ApiWriteOptions {
  /**
   * §06: PATCH REQUIRES `If-Match: <version>`; omitting it is 428
   * `precondition_required`, which is a client bug rather than a user-facing
   * condition. Typed as a number because that is what `version` is — building
   * the header string is this module's job, not a caller's.
   */
  ifMatch?: number;
  /**
   * §06: honoured on POST, keyed 24h. The caller owns the key's LIFETIME (one
   * key per submission, reused across retries of that submission) — see
   * `useIdempotencyKey`.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

function writeHeaders(options: ApiWriteOptions, hasBody: boolean): Record<string, string> {
  return {
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(options.ifMatch === undefined ? {} : { "If-Match": String(options.ifMatch) }),
    ...(options.idempotencyKey === undefined ? {} : { "Idempotency-Key": options.idempotencyKey }),
  };
}

/**
 * POST or PATCH. §06: a write returns the FULL resulting object, not 204, so
 * the response is parsed against the same read schema every list and detail
 * screen uses — which is what lets a mutation seed the cache with a value the
 * UI already knows how to render.
 */
export async function apiWrite<S extends z.ZodType>(
  schema: S,
  method: "POST" | "PATCH",
  path: string,
  body: unknown,
  options: ApiWriteOptions = {},
): Promise<z.infer<S>> {
  const url = `${API_BASE}${path}`;
  const raw = await request(url, path, {
    method,
    headers: writeHeaders(options, true),
    body: JSON.stringify(body),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  return lenientParse(schema, raw.body, path);
}

/** DELETE. Honours `If-Match` when the caller knows the version, which it always does here. */
export async function apiDelete(path: string, options: ApiWriteOptions = {}): Promise<void> {
  const url = `${API_BASE}${path}`;
  await request(url, path, {
    method: "DELETE",
    headers: writeHeaders(options, false),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
}
